import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Check, Home, Clock, AlertCircle, Search, User } from 'lucide-react';
import { isLeaseExpired } from '../../../models/tenantModel';

const ApartmentSelectDropdown = ({ apartments, value, onChange, disabled, t, isDarkMode }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Clear search term when closed
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    const calculateDaysLeft = (endDateStr) => {
        if (!endDateStr) return null;
        const end = new Date(endDateStr);
        // Set to 12:00 PM (noon) as per best practice
        end.setHours(12, 0, 0, 0);
        const now = new Date();
        const diff = end - now;
        // Use floors to get whole days, as noon to noon is exactly 1 day
        return Math.ceil(diff / (1000 * 60 * 60 * 24));
    };

    const formatAptDisplay = (a) => {
        return t('apartment_unit_display', {
            number: a.apartmentNumber,
            floor: a.floor,
            defaultValue: `Apt ${a.apartmentNumber} - Fl ${a.floor}`
        });
    };

    const filteredApartments = useMemo(() => {
        if (!searchTerm.trim()) return apartments;
        const lowerSearch = searchTerm.toLowerCase();
        return apartments.filter(a => {
            const display = formatAptDisplay(a).toLowerCase();
            const type = (a.type || '').toLowerCase();
            const size = (a.size || '').toString();
            return display.includes(lowerSearch) || type.includes(lowerSearch) || size.includes(lowerSearch);
        });
    }, [apartments, searchTerm, formatAptDisplay]);

    const availableNow = filteredApartments.filter(a => {
        const hasActiveTenants = a.tenants && a.tenants.length > 0 && !isLeaseExpired(a);
        return !hasActiveTenants && !a.isOwnerLiving;
    });

    const endingSoon = filteredApartments.filter(a => {
        const hasTenants = a.tenants && a.tenants.length > 0;
        // If lease is already considered expired by our central logic, it's not "ending soon", it's "available" (handled above)
        if (!hasTenants || a.isOwnerLiving || isLeaseExpired(a)) return false;

        // Only count as ending soon if there are NO future tenants scheduled
        if (a.tenants.length > 1) return false;

        const primaryTenant = a.tenants[0];
        if (!primaryTenant.contractEndDate) return false;

        const daysLeft = calculateDaysLeft(primaryTenant.contractEndDate);
        return daysLeft <= 60;
    });

    const currentlyOccupied = filteredApartments.filter(a => {
        const hasActiveTenants = a.tenants && a.tenants.length > 0 && !isLeaseExpired(a);
        const reflectsOwner = !!a.isOwnerLiving;

        if (reflectsOwner) return true;
        if (!hasActiveTenants) return false;

        // It's occupied if it's active AND not ending soon
        const primaryTenant = a.tenants[0];
        const isEndingSoon = a.tenants.length === 1 && primaryTenant.contractEndDate && calculateDaysLeft(primaryTenant.contractEndDate) <= 60;

        return !isEndingSoon;
    });

    const selectedApartment = apartments.find(a => a.id === value);

    const inputClasses = `w-full px-4 py-3 rounded-lg border text-start flex justify-between items-center transition-colors focus:outline-none focus:ring-2 focus:ring-red-500
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${isDarkMode ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'}
    `;

    const dropdownClasses = `absolute z-50 w-full mt-2 rounded-xl shadow-xl border overflow-hidden flex flex-col max-h-[400px]
        ${isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'}
    `;

    const itemClasses = (isSelected) => `w-full text-start px-4 py-3 border-b last:border-0 hover:bg-opacity-80 transition-colors flex items-center justify-between
        ${isDarkMode ? 'border-zinc-700 hover:bg-zinc-700' : 'border-gray-100 hover:bg-red-50'}
        ${isSelected ? (isDarkMode ? 'bg-zinc-700 border-s-4 border-s-red-500' : 'bg-red-50 border-s-4 border-s-red-500') : 'border-s-4 border-s-transparent'}
    `;

    const handleSelect = (id) => {
        onChange(id);
        setIsOpen(false);
    };

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                className={inputClasses}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <div className="flex flex-col">
                    <span className="font-medium">
                        {selectedApartment
                            ? formatAptDisplay(selectedApartment)
                            : t('select_unit')}
                    </span>
                    {selectedApartment && (
                        <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                            {selectedApartment.type} • {selectedApartment.size} m²
                        </span>
                    )}
                </div>
                <ChevronDown size={20} className={`${isDarkMode ? 'text-zinc-400' : 'text-gray-500'} ${isOpen ? 'transform rotate-180' : ''} transition-transform`} />
            </button>

            {isOpen && !disabled && (
                <div className={dropdownClasses} role="listbox" aria-label={t('apartment_element') || 'Apartment'}>
                    {/* Search Header */}
                    <div className={`p-2 border-b ${isDarkMode ? 'border-zinc-700 bg-zinc-800' : 'border-gray-200 bg-white'}`}>
                        <div className="relative">
                            <Search className={`absolute start-3 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-400'}`} size={16} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder={t('search_unit') || 'Search apartment...'}
                                autoFocus
                                className={`w-full ps-9 pe-4 py-2 text-sm rounded-lg outline-none transition-shadow focus:ring-2 focus:ring-red-500/50 
                                    ${isDarkMode ? 'bg-zinc-900 text-white placeholder-zinc-500' : 'bg-gray-100 text-gray-900 placeholder-gray-500'}`}
                            />
                        </div>
                    </div>

                    <div className="overflow-y-auto flex-1">
                        {filteredApartments.length === 0 && (
                            <div className={`p-6 flex flex-col items-center justify-center text-center ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                                <Search size={24} className="mb-2 opacity-50" />
                                <span>{t('no_apartments_found') || 'No suitable apartments found'}</span>
                            </div>
                        )}

                        {/* Available Now Group */}
                        {availableNow.length > 0 && (
                            <div className="mb-2">
                                <div className={`sticky top-0 px-4 py-2 text-xs font-bold uppercase tracking-wider shadow-sm z-10 flex items-center
                                    ${isDarkMode ? 'bg-zinc-900 text-green-400 border-b border-zinc-700' : 'bg-gray-50 flex text-green-700 border-b border-gray-200'}`}>
                                    <Home size={14} className="me-1.5" />
                                    {t('available_now') || 'Available Now'} ({availableNow.length})
                                </div>
                                <div>
                                    {availableNow.map(apt => (
                                        <button
                                            key={apt.id}
                                            type="button"
                                            onClick={() => handleSelect(apt.id)}
                                            className={itemClasses(value === apt.id)}
                                            role="option"
                                            aria-selected={value === apt.id}
                                        >
                                            <div className="flex flex-col items-start">
                                                <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                    {formatAptDisplay(apt)}
                                                </span>
                                                <div className={`text-sm mt-0.5 flex items-center space-x-2 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                                                    <span>{apt.type}</span>
                                                    <span>•</span>
                                                    <span>{apt.size} m²</span>
                                                    {apt.isFurnished && (
                                                        <>
                                                            <span>•</span>
                                                            <span className={isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}>{t('furnished') || 'Furnished'}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center">
                                                {value === apt.id && <Check size={18} className="text-red-500 me-2" />}
                                                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-400">
                                                    {t('ready') || 'Ready'}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Ending Soon Group */}
                        {endingSoon.length > 0 && (
                            <div className="mb-2">
                                <div className={`sticky top-0 px-4 py-2 text-xs font-bold uppercase tracking-wider shadow-sm z-10 flex items-center
                                    ${isDarkMode ? 'bg-zinc-900 text-amber-500 border-y border-zinc-700' : 'bg-gray-50 flex text-amber-600 border-y border-gray-200'}`}>
                                    <Clock size={14} className="me-1.5" />
                                    {t('lease_ending_soon') || 'Lease Ending Soon'} ({endingSoon.length})
                                </div>
                                <div>
                                    {endingSoon.map(apt => {
                                        const primaryTenant = apt.tenants[0];
                                        const daysLeft = calculateDaysLeft(primaryTenant.contractEndDate);
                                        const isOverdue = daysLeft < 0;
                                        return (
                                            <button
                                                key={apt.id}
                                                type="button"
                                                onClick={() => handleSelect(apt.id)}
                                                className={itemClasses(value === apt.id)}
                                                role="option"
                                                aria-selected={value === apt.id}
                                            >
                                                <div className="flex flex-col items-start">
                                                    <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {formatAptDisplay(apt)}
                                                    </span>
                                                    <div className={`text-sm mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                                                        <span>{apt.type} • {apt.size} m²</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center text-end">
                                                    {value === apt.id && <Check size={18} className="text-red-500 me-2" />}
                                                    <div className={`flex flex-col items-end`}>
                                                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${isOverdue ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400'}`}>
                                                            {isOverdue ? (t('overdue') || 'Overdue') : (t('ends_in_days', { count: daysLeft }) || `Ends in ${daysLeft}d`)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Currently Occupied Group */}
                        {currentlyOccupied.length > 0 && (
                            <div>
                                <div className={`sticky top-0 px-4 py-2 text-xs font-bold uppercase tracking-wider shadow-sm z-10 flex items-center
                                    ${isDarkMode ? 'bg-zinc-900 text-blue-400 border-y border-zinc-700' : 'bg-gray-50 flex text-blue-600 border-y border-gray-200'}`}>
                                    <User size={14} className="me-1.5" />
                                    {t('currently_occupied') || 'Currently Occupied'} ({currentlyOccupied.length})
                                </div>
                                <div>
                                    {currentlyOccupied.map(apt => {
                                        const hasMultipleLeases = apt.tenants && apt.tenants.length > 1;
                                        const isFutureOnly = apt.tenants && apt.tenants.length > 0 && new Date(apt.tenants[0].contractStartDate) > new Date();

                                        return (
                                            <button
                                                key={apt.id}
                                                type="button"
                                                onClick={() => handleSelect(apt.id)}
                                                className={itemClasses(value === apt.id)}
                                                role="option"
                                                aria-selected={value === apt.id}
                                            >
                                                <div className="flex flex-col items-start opacity-75">
                                                    <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                        {formatAptDisplay(apt)}
                                                    </span>
                                                    <div className={`text-sm mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                                                        <span>{apt.type} • {apt.size} m²</span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center text-end opacity-75">
                                                    {value === apt.id && <Check size={18} className="text-red-500 me-2" />}
                                                    <div className={`flex flex-col items-end`}>
                                                        {apt.isOwnerLiving ? (
                                                            <span className={`px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400 flex items-center`}>
                                                                <User size={12} className="me-1" />
                                                                {t('owner_living') || 'Owner Living'}
                                                            </span>
                                                        ) : isFutureOnly ? (
                                                            <div className="flex flex-col items-end gap-1">
                                                                <span className={`px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-400 flex items-center`}>
                                                                    <Clock size={10} className="me-1" />
                                                                    {t('future_tenant_only') || 'Future Lease Only'}
                                                                </span>
                                                            </div>
                                                        ) : hasMultipleLeases ? (
                                                            <div className="flex flex-col items-end gap-1">
                                                                <span className={`px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400`}>
                                                                    {t('occupied') || 'Occupied'}
                                                                </span>
                                                                <span className={`px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-400 flex items-center`}>
                                                                    <Clock size={10} className="me-1" />
                                                                    {t('future_tenant') || 'Future Tenant'}
                                                                </span>
                                                            </div>
                                                        ) : (
                                                            <span className={`px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400`}>
                                                                {t('occupied') || 'Occupied'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApartmentSelectDropdown;
